/**
 * MmConnectCard — "bookmarklet handshake" card.
 *
 * Ported from austin-file-splitter/public/index.html §bookmarkletCode.
 * The bookmarklet reads .ASPXAUTH + ASP.NET_SessionId from the MM portal
 * domain (amp4fun.magicmoneyllc.com) and POSTs them to /api/import/mm-connect
 * on Butler Buddy. Safari blocks cross-origin bookmarklet cookie access —
 * Chrome is the primary tested browser; note called out in SMART-IMPORT-COPY.md.
 *
 */

import { useMemo, useState, type FormEvent } from 'react'
import { Link2, Unlink, AlertTriangle } from 'lucide-react'
import { AgentBubble } from './AgentBubble'

interface MmConnectCardProps {
  onManualConnect: (auth: string, session: string, alb?: string) => Promise<void>
  busy: boolean
  error: string | null
}

export function MmConnectCard({
  onManualConnect,
  busy,
  error,
}: MmConnectCardProps) {
  const [showManual, setShowManual] = useState(false)
  const [auth, setAuth] = useState('')
  const [session, setSession] = useState('')
  const [alb, setAlb] = useState('')

  // The backend origin that receives the cookie POST. We key it off the
  // current window so this works equally for localhost dev (proxy through
  // vite) and production (sean.magicmeade.cloud).
  const backend = useMemo(
    () => (typeof window === 'undefined' ? '' : window.location.origin),
    [],
  )

  const bookmarklet = useMemo(() => {
    // Mirrors austin-file-splitter/public/index.html line 339 — the proven
    // .ASPXAUTH + ASP.NET_SessionId reader. Pointed at butler-buddy's
    // /api/import/mm-connect instead of austin's /api/connect.
    return `javascript:void((function(){var c=document.cookie.split(';').reduce(function(o,c){var p=c.trim().split('=');o[p[0]]=p.slice(1).join('=');return o;},{});var a=c['.ASPXAUTH'];var s=c['ASP.NET_SessionId'];if(!a){alert('Not logged into Magic Money. Please sign in first.');return;}fetch('${backend}/api/import/mm-connect',{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({auth:a,session:s||''})}).then(function(r){return r.json()}).then(function(d){if(d.ok){alert('Connected. Switch back to Butler Buddy.');}else{alert('Connection failed: '+(d.error||'Unknown error'));}}).catch(function(e){alert('Error: '+e.message);});})())`
  }, [backend])

  async function handleManualSubmit(e: FormEvent) {
    e.preventDefault()
    await onManualConnect(auth.trim(), session.trim(), alb.trim() || undefined)
  }

  return (
    <div className="w-full max-w-xl space-y-4">
      <AgentBubble from="agent">
                I need to borrow your Magic Money session to import barcodes. Two
        options: drag the button below to your bookmarks bar and click it
        while you're signed into the portal, or paste the cookies manually.
      </AgentBubble>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
          <Link2 className="h-4 w-4 text-carnival-400" />
          Connect to Magic Money
        </div>

        <div className="flex flex-col items-start gap-3">
          {/* eslint-disable-next-line react/jsx-no-script-url */}
          <a
            href={bookmarklet}
            className="inline-flex items-center gap-2 rounded-lg bg-carnival-400 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-carnival-500"
            onClick={(e) => {
              // Clicking in the Butler Buddy tab does nothing useful — the
              // bookmarklet only works when the active tab is on the MM
              // portal domain. Prevent navigation + remind the user.
              e.preventDefault()
              alert(
                'Drag this button to your bookmarks bar, then click it from the Magic Money portal tab.',
              )
            }}
            draggable
          >
                        ↪ Connect to Magic Money
          </a>

          <p className="text-xs text-slate-500 dark:text-slate-400">
                        Chrome works best. Safari blocks cross-origin cookie reads — if
            you're on Safari, use the manual paste option instead.
          </p>

          <button
            type="button"
            onClick={() => setShowManual((v) => !v)}
            className="text-xs text-outdoor-600 underline hover:text-outdoor-700 dark:text-outdoor-400"
          >
                        {showManual ? 'Hide manual paste' : 'Paste cookies manually'}
          </button>
        </div>

        {showManual ? (
          <form onSubmit={handleManualSubmit} className="mt-4 space-y-3 border-t border-slate-200 pt-4 dark:border-slate-700">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                .ASPXAUTH
              </label>
              <input
                type="text"
                value={auth}
                onChange={(e) => setAuth(e.target.value)}
                required
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-carnival-400 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                placeholder="paste the .ASPXAUTH cookie value"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                ASP.NET_SessionId
              </label>
              <input
                type="text"
                value={session}
                onChange={(e) => setSession(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-carnival-400 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                placeholder="optional"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
                AWSALB (optional)
              </label>
              <input
                type="text"
                value={alb}
                onChange={(e) => setAlb(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-carnival-400 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                placeholder="if the portal's behind an ALB"
              />
            </div>
            {error ? (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}
            <button
              type="submit"
              disabled={busy || !auth.trim()}
              className="w-full rounded-lg bg-outdoor-500 py-2 text-sm font-medium text-white hover:bg-outdoor-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Connecting…' : 'Connect'}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  )
}

interface MmDisconnectRowProps {
  onDisconnect: () => void
  connectedAt?: number
}

export function MmDisconnectRow({ onDisconnect, connectedAt }: MmDisconnectRowProps) {
  const since = connectedAt
    ? new Date(connectedAt).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      })
    : null
  return (
    <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                Connected to Magic Money{since ? ` · since ${since}` : ''}
      </span>
      <button
        type="button"
        onClick={onDisconnect}
        className="inline-flex items-center gap-1 text-[11px] text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100"
      >
        <Unlink className="h-3 w-3" /> Disconnect
      </button>
    </div>
  )
}
